import React, { useState, useEffect, useRef } from "react";
import PropTypes from "prop-types";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import api from "../../../../utils/api";
import {
    createDraftFromJson,
    createDraftFromShopifyTemplateAi,
    createDraftFromItinerary,
    createSmartCampaignDraft,
    instantiateCampaignTemplate,
    listShopifyAiProducts,
    listCampaignTemplates,
    validateCreateDraftFromJson
} from "../../../../services/campaigns-service";
import { readAndValidatePortableImportJsonFile } from "../../../../utils/portable-campaign-import-file";
import ImportCampaignJsonPanel from "../campaign-steps/ImportCampaignJsonPanel";
import { buildScopedPath } from "../../../../utils/account-context";
import { useActiveAccountId } from "../../../../context/account-branding-context";
import ItineraryDraftPanel from "../campaign-steps/ItineraryDraftPanel";
import SmartCampaignDraftPanel from "../campaign-steps/SmartCampaignDraftPanel";
import DesignerAgentPromptComposer from "../designer-agent/DesignerAgentPromptComposer";
import CreateDesignDialog from "./CreateDesignDialog";
import UpgradeDrawer from "../../../../components/saas/UpgradeDrawer";
import { useShopifyCreateGate } from "../../../../hooks/useShopifyCreateGate";
import { useSmartCampaignFeatureToggle } from "../../../../hooks/useSmartCampaignFeatureToggle";
import { useSaasEntitlements } from "../../../../hooks/useSaasEntitlements";
import { isItineraryDraftEntryEnabled } from "../../../../constants/campaignFeatureFlags";
import { resolveItineraryFriendlyErrorMessage } from "../../../../utils/itinerary-draft-error-message";
import { buildAiCampaignSuccessMessage } from "../../../../utils/ai-token-usage-message";
import {
    extractSaasPaywallCode,
    resolveSaasErrorCopy,
    resolveSaasErrorMessage,
} from "../../../../utils/saasErrorCopy";

export const RATIO_OPTIONS = [
    { ratio: "16:9", label: "Landscape" },
    { ratio: "9:16", label: "Portrait" },
    { ratio: "1:1", label: "Square" }
];

export const getDefaultCampaignTitle = () => `Untitled Campaign ${new Date().toLocaleDateString()}`;

const normalizeDraftNavigationPath = (pathname) => {
    if (typeof pathname !== "string" || !pathname.trim()) {
        return null;
    }
    const trimmed = pathname.trim();
    const withoutAccountsPrefix = trimmed.replace(/^\/accounts\/\d+(?=\/)/, "");
    return withoutAccountsPrefix || trimmed;
};

const getDraftNavigationPathFromAiResponse = (response) => {
    if (!response || typeof response !== "object") {
        return null;
    }
    const nextUrl = response.next_url || response.nextUrl || response.redirect_url || response.redirectUrl;
    if (typeof nextUrl === "string" && nextUrl.trim()) {
        return normalizeDraftNavigationPath(nextUrl);
    }
    const rawDraftId =
        response.draftId ||
        response.draft_id ||
        response?.draft?.id ||
        response?.campaign?.id ||
        response?.createdDraft?.id;
    if (rawDraftId) {
        return `/edit-draft-campaigns/${rawDraftId}`;
    }
    return null;
};

const CreateDesignEntryFlow = ({
    isOpen,
    onCancel,
    onPaywall = null,
    campaignTitle = "",
    campaignDescription = ""
}) => {
    const navigate = useNavigate();
    const activeAccountId = useActiveAccountId();

    const [isImporting, setIsImporting] = useState(false);
    const [isGeneratingAiDraft, setIsGeneratingAiDraft] = useState(false);
    const [isGeneratingItineraryDraft, setIsGeneratingItineraryDraft] = useState(false);
    const [isGeneratingSmartCampaignDraft, setIsGeneratingSmartCampaignDraft] = useState(false);
    const [itineraryAspectRatio, setItineraryAspectRatio] = useState("16:9");
    const [itineraryCampaignTitle, setItineraryCampaignTitle] = useState("");
    const [smartCampaignPrompt, setSmartCampaignPrompt] = useState("");
    const [importJson, setImportJson] = useState("");
    const [importFileLabel, setImportFileLabel] = useState("");
    const importFileInputRef = useRef(null);
    const [importValidation, setImportValidation] = useState(null);
    const [templateOptions, setTemplateOptions] = useState([]);
    const [isLoadingTemplates, setIsLoadingTemplates] = useState(false);
    const [shopifyProducts, setShopifyProducts] = useState([]);
    const [selectedShopifyProductHandle, setSelectedShopifyProductHandle] = useState("");
    const [selectedShopifyTemplateId, setSelectedShopifyTemplateId] = useState("");
    const [createEntryMode, setCreateEntryMode] = useState("blank");
    const [shopifyEntryMode, setShopifyEntryMode] = useState("template");
    const [selectedEntryAspectRatio, setSelectedEntryAspectRatio] = useState("");
    const [selectedEntryTemplateId, setSelectedEntryTemplateId] = useState(null);
    const [isEntrySubmitting, setIsEntrySubmitting] = useState(false);
    const [upgradeDrawer, setUpgradeDrawer] = useState({ open: false, code: null });
    const { isEnabled: isShopifyFeatureEnabled } = useShopifyCreateGate(activeAccountId);
    const { isEnabled: isSmartCampaignFeatureEnabled } = useSmartCampaignFeatureToggle(activeAccountId);
    const { entitlements: saasEntitlements } = useSaasEntitlements(activeAccountId);
    const viewerRole = React.useMemo(() => {
        try {
            return JSON.parse(localStorage.getItem("userdata") || "{}")?.role || "";
        } catch {
            return "";
        }
    }, []);
    const isSuperAdminViewer = viewerRole === "SuperAdmin";
    const lockPremiumCreateModes = Boolean(
        saasEntitlements
        && !saasEntitlements.isPremium
        && !saasEntitlements.isEnterprise
    );

    const emitPaywall = (code, payload = {}) => {
        if (!code) {
            return false;
        }
        if (code === "FREE_CREATE_MODE_DENIED") {
            setUpgradeDrawer({ open: true, code });
            return true;
        }
        if (typeof onPaywall === "function") {
            onPaywall(code, payload);
            return true;
        }
        if (typeof window !== "undefined") {
            window.dispatchEvent(
                new CustomEvent("tv-saas-paywall", {
                    detail: payload.paywall || { code, message: payload.message },
                })
            );
            return true;
        }
        return false;
    };

    const handleSaasCreateError = (errorOrPayload, fallbackMessage) => {
        const payload = errorOrPayload?.response?.data || errorOrPayload || {};
        const paywallCode = extractSaasPaywallCode(payload);
        if (paywallCode && emitPaywall(paywallCode, payload)) {
            return true;
        }
        const mapped = paywallCode ? resolveSaasErrorCopy(paywallCode) : null;
        toast.error(mapped?.body || fallbackMessage || resolveSaasErrorMessage(payload) || "Request failed.");
        return false;
    };

    const resolvedCampaignTitle = campaignTitle.trim() || getDefaultCampaignTitle();
    const resolvedCampaignDescription = campaignDescription.trim() || "";

    const createBlankDraftAndOpenEditor = async (aspectRatio, sourceContext = null) => {
        if (!aspectRatio) {
            toast.error("Select aspect ratio to continue.");
            return;
        }
        setIsEntrySubmitting(true);
        try {
            const formData = new FormData();
            formData.append("title", resolvedCampaignTitle);
            formData.append("theme", resolvedCampaignDescription);
            formData.append("count", "0");
            formData.append("aspectRatio", aspectRatio);
            if (sourceContext?.source_channel) {
                formData.append("source_channel", sourceContext.source_channel);
            }
            if (sourceContext?.source_type) {
                formData.append("source_type", sourceContext.source_type);
            }
            const res = await api.post("/campaigns/createdDraftCampaign", formData);
            if (!res.data.success) throw new Error();
            const draftId = res.data.draftId || res.data.campaign?.id;
            if (draftId) {
                navigate(buildScopedPath(activeAccountId, `/edit-draft-campaigns/${draftId}`));
                return;
            }
            toast.error("Unable to open draft editor.");
        } catch (error) {
            const payload = error?.response?.data || {};
            handleSaasCreateError(payload?.code ? payload : error, "Failed to create draft.");
        } finally {
            setIsEntrySubmitting(false);
        }
    };

    const parsePastedImportJson = () => {
        if (!importJson.trim()) {
            throw new Error("Imported JSON is required");
        }
        let parsedJson;
        try {
            parsedJson = JSON.parse(importJson);
        } catch {
            throw new Error("Imported JSON is not valid");
        }
        if (parsedJson === null || typeof parsedJson !== "object" || Array.isArray(parsedJson)) {
            throw new Error("Portable import requires a JSON object");
        }
        return parsedJson;
    };

    const buildImportPayload = () => {
        const parsedJson = parsePastedImportJson();
        return {
            campaign_name: resolvedCampaignTitle,
            campaign_description: resolvedCampaignDescription,
            strict_mode: true,
            portable_settings: parsedJson
        };
    };

    const clearImportFileInput = () => {
        if (importFileInputRef.current) {
            importFileInputRef.current.value = "";
        }
        setImportFileLabel("");
    };

    const handleImportJsonTextChange = (value) => {
        setImportJson(value);
        setImportValidation(null);
    };

    const handleImportFileSelected = async (e) => {
        const file = e?.target?.files?.[0];
        if (!file) {
            return;
        }
        setImportValidation(null);
        const result = await readAndValidatePortableImportJsonFile(file);
        if (!result.ok) {
            toast.error(result.error || "Invalid import file");
            clearImportFileInput();
            return;
        }
        setImportJson(JSON.stringify(result.data, null, 2));
        setImportFileLabel(file.name);
        toast.success("JSON file loaded. Review below, then validate or import.");
    };

    const handleValidateImport = async () => {
        try {
            const payload = buildImportPayload();
            setIsImporting(true);
            const result = await validateCreateDraftFromJson(payload);
            if (result?.error) {
                toast.error(result.error);
                setImportValidation(result?.details || null);
                return;
            }
            setImportValidation(result);
            toast.success("Import JSON validated successfully");
        } catch (error) {
            toast.error(error.message || "Failed to validate import JSON");
        } finally {
            setIsImporting(false);
        }
    };

    const handleImportCreateDraft = async () => {
        try {
            const payload = buildImportPayload();
            setIsImporting(true);
            const result = await createDraftFromJson(payload);
            if (result?.error) {
                toast.error(result.error);
                return;
            }
            toast.success("Draft created from imported JSON");
            if (result?.next_url) {
                navigate(buildScopedPath(activeAccountId, result.next_url));
            }
        } catch (error) {
            toast.error(error.message || "Failed to import JSON");
        } finally {
            setIsImporting(false);
        }
    };

    const loadTemplateOptions = async () => {
        setIsLoadingTemplates(true);
        const response = await listCampaignTemplates({ limit: 100 });
        setIsLoadingTemplates(false);
        if (response?.error) {
            toast.error(response.error || "Failed to load templates");
            return;
        }
        const normalizedTemplates = Array.isArray(response?.templates)
            ? response.templates.map((template) => template?.dataValues || template)
            : [];
        setTemplateOptions(normalizedTemplates);
    };

    const loadShopifyProducts = async () => {
        const response = await listShopifyAiProducts({ limit: 300 });
        if (response?.error) {
            toast.error(response.error || "Failed to load Shopify products");
            return;
        }
        setShopifyProducts(response?.products || []);
    };

    useEffect(() => {
        if (!isOpen) {
            return;
        }
        if ((createEntryMode === "template" || createEntryMode === "ai") && templateOptions.length === 0) {
            loadTemplateOptions();
        }
        if (isShopifyFeatureEnabled && createEntryMode === "ai" && shopifyProducts.length === 0) {
            loadShopifyProducts();
        }
    }, [isOpen, createEntryMode, isShopifyFeatureEnabled]); // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
        if (isShopifyFeatureEnabled) {
            return;
        }
        if (createEntryMode === "ai") {
            setCreateEntryMode("blank");
        }
    }, [isShopifyFeatureEnabled, createEntryMode]);

    const handleGenerateItineraryDraft = async ({
        inputMode,
        file,
        emailText,
        aspectRatio,
        title,
        mediaMode,
        usePersonalizedVoiceover,
    }) => {
        setIsGeneratingItineraryDraft(true);
        try {
            const formData = new FormData();
            formData.append("aspectRatio", aspectRatio || itineraryAspectRatio || "16:9");
            formData.append("inputMode", inputMode);
            formData.append("mediaMode", mediaMode || "image");
            formData.append("usePersonalizedVoiceover", String(usePersonalizedVoiceover !== false));
            if (title) {
                formData.append("title", title);
            }
            if (file) {
                formData.append("file", file);
            }
            if (emailText) {
                formData.append("emailText", emailText);
            }

            const response = await createDraftFromItinerary(formData);
            if (response?.error) {
                if (handleSaasCreateError(response, resolveItineraryFriendlyErrorMessage(response))) {
                    return false;
                }
                return false;
            }

            toast.success(buildAiCampaignSuccessMessage("Itinerary draft created.", response));
            const nextPath = getDraftNavigationPathFromAiResponse(response);
            const draftId = response.draftId || response.draft_id || response.campaign_draft_id;
            const editorPath = nextPath || (draftId ? `/edit-draft-campaigns/${draftId}` : null);
            if (editorPath) {
                navigate(buildScopedPath(activeAccountId, editorPath));
            } else {
                toast.warn("Draft created but editor could not be opened. Open it from Campaigns → Drafts.");
            }
            return true;
        } catch (error) {
            handleSaasCreateError(error, "Failed to generate itinerary draft.");
            return false;
        } finally {
            setIsGeneratingItineraryDraft(false);
        }
    };

    const handleGenerateSmartCampaignDraft = async ({
        prompt,
        files = [],
        aspectRatio,
        title,
    }) => {
        setIsGeneratingSmartCampaignDraft(true);
        try {
            const formData = new FormData();
            formData.append("aspectRatio", aspectRatio || "16:9");
            if (prompt) {
                formData.append("prompt", prompt);
            }
            if (title) {
                formData.append("title", title);
            }
            files.forEach((file) => {
                formData.append("files", file);
            });

            const response = await createSmartCampaignDraft(formData);
            if (response?.error) {
                handleSaasCreateError(response, "Failed to generate AI campaign draft.");
                return false;
            }

            toast.success(buildAiCampaignSuccessMessage("AI campaign draft created.", response));
            const warningMessage = Array.isArray(response?.warnings)
                ? response.warnings.find((warning) => warning?.message)?.message
                : null;
            if (warningMessage) {
                toast.warn(warningMessage);
            }
            const nextPath = getDraftNavigationPathFromAiResponse(response);
            const draftId = response.draftId || response.draft_id || response.campaign_draft_id;
            const editorPath = nextPath || (draftId ? `/edit-draft-campaigns/${draftId}` : null);
            if (editorPath) {
                navigate(buildScopedPath(activeAccountId, editorPath));
            } else {
                toast.warn("Draft created but editor could not be opened. Open it from Campaigns → Drafts.");
            }
            return true;
        } catch (error) {
            handleSaasCreateError(error, "Failed to generate AI campaign draft.");
            return false;
        } finally {
            setIsGeneratingSmartCampaignDraft(false);
        }
    };

    const handleGenerateFromShopifyProductTemplate = async () => {
        if (!selectedShopifyProductHandle) {
            toast.error("Select Shopify product");
            return false;
        }
        if (!selectedShopifyTemplateId) {
            toast.error("Select template");
            return false;
        }
        setIsGeneratingAiDraft(true);
        const response = await createDraftFromShopifyTemplateAi({
            campaignName: resolvedCampaignTitle,
            campaignDescription: resolvedCampaignDescription,
            productHandle: selectedShopifyProductHandle,
            templateId: Number(selectedShopifyTemplateId)
        });
        setIsGeneratingAiDraft(false);
        if (response?.error) {
            handleSaasCreateError(response, "Failed to generate Shopify draft");
            return false;
        }

        const draftPath = getDraftNavigationPathFromAiResponse(response);
        if (!draftPath) {
            toast.error("Draft generated, but editor link is missing in API response.");
            return false;
        }
        toast.success(buildAiCampaignSuccessMessage("Shopify draft generated.", response));
        navigate(buildScopedPath(activeAccountId, draftPath));
        return true;
    };

    const handleCreateFromEntry = async (requestedMode) => {
        const effectiveMode = requestedMode || (createEntryMode === "ai" ? shopifyEntryMode : createEntryMode);
        if (effectiveMode === "blank") {
            const sourceContext = createEntryMode === "ai"
                ? { source_channel: "shopify", source_type: "design_scratch" }
                : null;
            await createBlankDraftAndOpenEditor(selectedEntryAspectRatio, sourceContext);
            return;
        }
        if (effectiveMode === "template") {
            const templateIdToUse = createEntryMode === "ai"
                ? selectedShopifyTemplateId
                : selectedEntryTemplateId;
            if (!templateIdToUse) return;
            setIsEntrySubmitting(true);
            const response = await instantiateCampaignTemplate(templateIdToUse, {
                campaign_name: resolvedCampaignTitle,
                ...(createEntryMode === "ai"
                    ? { source_channel: "shopify", source_type: "template" }
                    : {})
            });
            setIsEntrySubmitting(false);
            if (response?.error) {
                toast.error(response.error || "Failed to use template.");
                return;
            }
            if (response?.next_url) {
                navigate(buildScopedPath(activeAccountId, response.next_url));
            }
            return;
        }
        if (effectiveMode === "ai") {
            setIsEntrySubmitting(true);
            await handleGenerateFromShopifyProductTemplate();
            setIsEntrySubmitting(false);
        }
    };

    const shopifyTemplateOptions = templateOptions.filter((template) =>
        isShopifyFeatureEnabled &&
        String(template?.category_key || template?.category || "").toLowerCase() === "shopify"
    );
    const visibleTemplateOptions = templateOptions.filter((template) => {
        const templateCategory = String(template?.category_key || template?.category || "").toLowerCase();
        if (!isShopifyFeatureEnabled && templateCategory === "shopify") {
            return false;
        }
        return true;
    });

    const entryTemplates = createEntryMode === "blank" && selectedEntryAspectRatio
        ? visibleTemplateOptions.filter((template) => template.aspect_ratio === selectedEntryAspectRatio)
        : visibleTemplateOptions;
    const effectiveEntryMode = createEntryMode === "ai" ? shopifyEntryMode : createEntryMode;
    const isCreateDisabled =
        isEntrySubmitting ||
        isGeneratingAiDraft ||
        (effectiveEntryMode === "blank" && !selectedEntryAspectRatio) ||
        (effectiveEntryMode === "template" && !(createEntryMode === "ai" ? selectedShopifyTemplateId : selectedEntryTemplateId)) ||
        (effectiveEntryMode === "ai" && (!selectedShopifyProductHandle || !selectedShopifyTemplateId)) ||
        (effectiveEntryMode === "itinerary" && !itineraryAspectRatio);

    return (
        <>
        <CreateDesignDialog
            isOpen={isOpen}
            activeTab={createEntryMode}
            onTabChange={setCreateEntryMode}
            ratioOptions={RATIO_OPTIONS}
            selectedAspectRatio={selectedEntryAspectRatio}
            onSelectAspectRatio={setSelectedEntryAspectRatio}
            templates={entryTemplates}
            isLoadingTemplates={isLoadingTemplates}
            onSelectTemplate={setSelectedEntryTemplateId}
            selectedTemplateId={selectedEntryTemplateId}
            shopifyProducts={shopifyProducts}
            selectedShopifyProductHandle={selectedShopifyProductHandle}
            onSelectShopifyProductHandle={setSelectedShopifyProductHandle}
            shopifyTemplates={shopifyTemplateOptions}
            selectedShopifyTemplateId={selectedShopifyTemplateId}
            onSelectShopifyTemplateId={setSelectedShopifyTemplateId}
            shopifyCreateMode={shopifyEntryMode}
            onShopifyCreateModeChange={setShopifyEntryMode}
            onCreate={handleCreateFromEntry}
            onCancel={onCancel}
            importJsonContent={(
                <ImportCampaignJsonPanel
                    importJson={importJson}
                    importFileLabel={importFileLabel}
                    fileInputRef={importFileInputRef}
                    importValidation={importValidation}
                    isImporting={isImporting}
                    onImportJsonChange={handleImportJsonTextChange}
                    onImportFileSelected={handleImportFileSelected}
                    onValidateImport={handleValidateImport}
                    onImportCreateDraft={handleImportCreateDraft}
                />
            )}
            isCreateDisabled={isCreateDisabled}
            isSubmitting={isEntrySubmitting || isGeneratingAiDraft || isGeneratingItineraryDraft || isGeneratingSmartCampaignDraft}
            showShopifyOption={isShopifyFeatureEnabled}
            itineraryEntryEnabled={isItineraryDraftEntryEnabled() || lockPremiumCreateModes}
            smartCampaignEntryEnabled={isSmartCampaignFeatureEnabled || lockPremiumCreateModes}
            designerAgentEntryEnabled
            showImportJsonTab={isSuperAdminViewer}
            lockPremiumCreateModes={lockPremiumCreateModes}
            onPremiumLockedTab={(code) => emitPaywall(code)}
            itineraryContent={(
                <ItineraryDraftPanel
                    aspectRatio={itineraryAspectRatio}
                    onAspectRatioChange={setItineraryAspectRatio}
                    campaignTitle={itineraryCampaignTitle}
                    onCampaignTitleChange={setItineraryCampaignTitle}
                    onGenerateItineraryDraft={handleGenerateItineraryDraft}
                    isGeneratingItineraryDraft={isGeneratingItineraryDraft}
                />
            )}
            smartCampaignContent={(
                <SmartCampaignDraftPanel
                    prompt={smartCampaignPrompt}
                    onPromptChange={setSmartCampaignPrompt}
                    onGenerateSmartCampaignDraft={handleGenerateSmartCampaignDraft}
                    isGeneratingSmartCampaignDraft={isGeneratingSmartCampaignDraft}
                />
            )}
            designerAgentContent={(
                <DesignerAgentPromptComposer
                    variant="dialog"
                    onCreated={(data) => {
                        const path = data?.draftPath
                            || getDraftNavigationPathFromAiResponse(data);
                        if (path) {
                            onCancel();
                            navigate(buildScopedPath(activeAccountId, path));
                        }
                    }}
                />
            )}
        />
        <UpgradeDrawer
            open={upgradeDrawer.open}
            code={upgradeDrawer.code}
            accountId={activeAccountId}
            overlayLayer="modal"
            onClose={() => setUpgradeDrawer({ open: false, code: null })}
        />
        </>
    );
};

CreateDesignEntryFlow.propTypes = {
    isOpen: PropTypes.bool.isRequired,
    onCancel: PropTypes.func.isRequired,
    onPaywall: PropTypes.func,
    campaignTitle: PropTypes.string,
    campaignDescription: PropTypes.string
};

export default CreateDesignEntryFlow;
